import { useState, useEffect, useCallback } from "react";
import { Loader2, Lock, RefreshCw, Download, FileDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const SECTION_LABELS = {
  hero: "1 · Hero",
  problem: "2 · Le problème",
  steps: "3 · Les 3 étapes",
  wall: "4 · Le mur",
  contrast: "5 · Avant/après",
  faq: "7 · FAQ",
  navbar: "Barre de navigation",
  "page-cta": "Pages secondaires",
  direct: "Sans clic (formulaire direct)",
};

const LeadRow = ({ lead: l }) => (
  <tr className="border-t border-[#ECECEC]" data-testid={`lead-row-${l.id}`}>
    <td className="px-4 py-3 font-semibold text-[#0A2342]">{l.company || "-"}</td>
    <td className="px-4 py-3 text-[#3A3A3A]">
      <div>{l.name || "-"}</div>
      <a href={`mailto:${l.email}`} className="text-[#0047AB] hover:underline">{l.email}</a>
    </td>
    <td className="px-4 py-3 text-[#4A4A4A]">{l.role || "-"}</td>
    <td className="px-4 py-3 text-[#4A4A4A] uppercase">{l.language}</td>
    <td className="px-4 py-3 text-[#4A4A4A] whitespace-nowrap">{fmtDate(l.created_at)}</td>
    <td className="px-4 py-3 whitespace-nowrap">
      {l.downloaded_at ? (
        <span className="inline-flex items-center gap-1.5 text-[#10B981] font-semibold">
          <Download className="w-4 h-4" />
          {fmtDate(l.downloaded_at)}
        </span>
      ) : (
        <span className="text-[#8A8A8A]">Pas encore</span>
      )}
    </td>
  </tr>
);

const LeadsPage = () => {
  const [password, setPassword] = useState(() => sessionStorage.getItem("blog-admin-pw") || "");
  const [pwInput, setPwInput] = useState("");
  const [isAuth, setIsAuth] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [days, setDays] = useState(30);

  const fetchLeads = useCallback(async (pw) => {
    setLoading(true);
    try {
      const headers = { "x-admin-password": pw };
      const [briefs, cta] = await Promise.all([
        axios.get(`${API}/brief-requests`, { headers }),
        axios.get(`${API}/analytics/cta?days=${days}`, { headers }),
      ]);
      setLeads(briefs.data);
      setAnalytics(cta.data);
      setIsAuth(true);
      sessionStorage.setItem("blog-admin-pw", pw);
      setPassword(pw);
    } catch (e) {
      setIsAuth(false);
      if (e.response?.status === 401) toast.error("Mot de passe incorrect");
      else toast.error("Impossible de charger les leads");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    if (password) fetchLeads(password);
  }, [password, days, fetchLeads]);

  const exportCsv = () => {
    const head = ["Date", "Nom", "Email", "Entreprise", "Fonction", "Langue", "Téléchargé le"];
    const rows = leads.map((l) => [
      l.created_at || "",
      l.name || "",
      l.email || "",
      l.company || "",
      l.role || "",
      l.language || "",
      l.downloaded_at || "",
    ]);
    const csv = [head, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `cayus-leads-dossier-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAnalyticsCsv = () => {
    const head = ["Section", "Clics", "Demandes de démo", "Taux de conversion (%)"];
    const rows = (analytics?.sections || []).map((r) => [r.section, r.clicks, r.demos, r.rate ?? ""]);
    const csv = [head, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `cayus-sections-${days}j-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isAuth) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] flex items-center justify-center px-4" data-testid="leads-login">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            fetchLeads(pwInput);
          }}
          className="w-full max-w-sm bg-white border border-[#C3CFE0] rounded-xl p-8"
        >
          <div className="w-12 h-12 bg-[#0047AB]/10 rounded-lg flex items-center justify-center mb-5">
            <Lock className="w-6 h-6 text-[#0047AB]" />
          </div>
          <h1 className="font-extrabold text-2xl text-[#0A2342] mb-2">Leads et dossier exécutif</h1>
          <p className="text-sm text-[#4A4A4A] mb-6">Page privée. Entrez le mot de passe administrateur.</p>
          <Input
            type="password"
            value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            placeholder="Mot de passe"
            className="mb-4"
            data-testid="leads-password-input"
          />
          <Button
            type="submit"
            disabled={loading}
            className="w-full rounded-md bg-[#0047AB] text-white font-semibold hover:bg-[#0B3F8F]"
            data-testid="leads-login-button"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Entrer"}
          </Button>
        </form>
      </div>
    );
  }

  const downloaded = leads.filter((l) => l.downloaded_at).length;

  return (
    <div className="min-h-screen bg-[#F7F9FC] py-12" data-testid="leads-page">
      <div className="wrap-cayus">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="mono-cayus text-[#0047AB] mb-2">Page privée</p>
            <h1 className="font-extrabold text-3xl sm:text-4xl text-[#0A2342] tracking-tight">Leads &amp; conversion</h1>
            <p className="text-sm text-[#4A4A4A] mt-2" data-testid="leads-count">
              {leads.length} demande{leads.length > 1 ? "s" : ""} · {downloaded} téléchargement{downloaded > 1 ? "s" : ""} confirmé{downloaded > 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => fetchLeads(password)}
              className="rounded-md border-[#C3CFE0]"
              data-testid="leads-refresh-button"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Rafraîchir
            </Button>
            <Button
              onClick={exportCsv}
              disabled={leads.length === 0}
              className="rounded-md bg-[#0047AB] text-white hover:bg-[#0B3F8F]"
              data-testid="leads-export-button"
            >
              <FileDown className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Sections qui convertissent */}
        <div className="bg-white border border-[#C3CFE0] rounded-xl p-6 sm:p-7 mb-10" data-testid="cta-analytics">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <p className="mono-cayus text-[#0047AB] mb-1.5">Sections qui convertissent</p>
              <p className="text-sm text-[#4A4A4A]">
                {analytics
                  ? `${analytics.total_clicks} clic${analytics.total_clicks > 1 ? "s" : ""} sur « Réserver ma démo » · ${analytics.total_demos} demande${analytics.total_demos > 1 ? "s" : ""} de démo`
                  : "Chargement…"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                    days === d ? "bg-[#0047AB] text-white border-[#0047AB]" : "bg-white text-[#4A4A4A] border-[#C3CFE0] hover:border-[#0047AB]"
                  }`}
                  data-testid={`analytics-days-${d}`}
                >
                  {d} j
                </button>
              ))}
              <Button
                onClick={exportAnalyticsCsv}
                disabled={!analytics?.sections?.length}
                variant="outline"
                className="rounded-md border-[#C3CFE0] ml-1"
                data-testid="analytics-export-button"
              >
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
            </div>
          </div>

          {analytics && analytics.sections.length === 0 ? (
            <p className="text-sm text-[#8A8A8A]" data-testid="analytics-empty">
              Aucun clic enregistré sur cette période.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {(analytics?.sections || []).map((r) => {
                const max = Math.max(...analytics.sections.map((x) => x.clicks), 1);
                return (
                  <div key={r.section} className="flex items-center gap-4" data-testid={`analytics-row-${r.section}`}>
                    <span className="w-28 sm:w-36 text-sm font-semibold text-[#0A2342] truncate">{SECTION_LABELS[r.section] || r.section}</span>
                    <div className="flex-1 h-7 bg-[#F3F3F3] rounded-md overflow-hidden relative">
                      <div
                        className="h-full bg-[linear-gradient(90deg,#5EB3F6,#0047AB)] transition-all duration-700"
                        style={{ width: `${(r.clicks / max) * 100}%` }}
                      />
                      <span className="absolute inset-y-0 left-3 flex items-center text-xs font-semibold text-[#0A2342] mix-blend-luminosity">
                        {r.clicks} clic{r.clicks > 1 ? "s" : ""}
                      </span>
                    </div>
                    <span className="w-20 text-sm text-[#4A4A4A] text-right">{r.demos} démo{r.demos > 1 ? "s" : ""}</span>
                    <span className={`w-16 text-sm font-bold text-right ${r.rate ? "text-[#047857]" : "text-[#8A8A8A]"}`}>
                      {r.rate !== null && r.rate !== undefined ? `${r.rate} %` : "-"}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <h2 className="font-bold text-xl text-[#0A2342] mb-4">Demandes de dossier exécutif</h2>
        {leads.length === 0 ? (
          <div className="bg-white border border-[#C3CFE0] rounded-xl p-12 text-center" data-testid="leads-empty">
            <p className="text-[#4A4A4A]">Aucune demande pour l'instant.</p>
          </div>
        ) : (
          <div className="bg-white border border-[#C3CFE0] rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F3F3F3] text-left text-[#0A2342]">
                  <th className="px-4 py-3 font-bold">Entreprise</th>
                  <th className="px-4 py-3 font-bold">Contact</th>
                  <th className="px-4 py-3 font-bold">Fonction</th>
                  <th className="px-4 py-3 font-bold">Langue</th>
                  <th className="px-4 py-3 font-bold">Demandé le</th>
                  <th className="px-4 py-3 font-bold">Téléchargé</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <LeadRow key={l.id} lead={l} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadsPage;
